import {
  ConfigLocation,
  ConfigMetric,
  NextFiscalWeek,
  PendingTarget,
  TargetsMap,
} from './configTypes';

export const nationalKey = (org: string, dept: string): string => `national:${org}:${dept}`;

export const locationKey = (location: string, dept: string): string => `location:${location}:${dept}`;

/** Cache key of the Onboarding Ramp in Redux. `appliesTo` is `'national'` or a location name. */
export const rampKey = (org: string, dept: string, appliesTo: string): string =>
  appliesTo === 'national' ? nationalKey(org, dept) : `location:${org}:${appliesTo}:${dept}`;

export const pluralize = (n: number | string, unit: string): string =>
  `${n} ${unit}${Number(n) === 1 ? '' : 's'}`;

/** Cadence and escalation values are whole numbers of 1 or more. */
export const isWholeNumber = (v: string): boolean =>
  v.trim() !== '' && Number.isInteger(Number(v)) && Number(v) >= 1;

/** Locations of the org where the selected department is set up in Admin Location Management. */
export const getScopeLocations = (
  locations: ConfigLocation[],
  org: string,
  dept: string
): ConfigLocation[] => locations.filter((l) => l.org === org && l.departments.includes(dept));

/** Backend validation errors arrive as `{ message }`; anything else gets a generic retry message. */
export const getApiErrorMessage = (
  err: unknown,
  fallback = 'Could not save. Your changes are kept, please try again.'
): string => {
  const data = (err as { response?: { data?: { message?: unknown } } })?.response?.data;
  return typeof data?.message === 'string' && data.message ? data.message : fallback;
};

export const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

// ─── Metrics and fiscal weeks ────────────────────────────────────────────────

/** Formats a metric value the way the mockup does: BPA 2 decimals, Cases Picked whole, others 1 decimal. */
export const formatMetricValue = (metric: ConfigMetric, v: number | null | undefined): string => {
  const n = Number(v);
  if (v === null || v === undefined || !Number.isFinite(n)) return 'Not set';
  const s = metric.id === 'BPA' ? n.toFixed(2) : metric.id === 'Cases Picked' ? String(Math.round(n)) : n.toFixed(1);
  return metric.unit ? `${s} ${metric.unit}` : s;
};

const toIso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const formatShortDate = (d: Date): string =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Start of the next fiscal week. Fiscal weeks start on Sunday; a period has 4 weeks and a year 13 periods.
 * `current` is the current period/week from the app (state.appData.periodWeek.data); when it is not
 * loaded yet the label is left out and only the date is shown.
 * TODO(api): replace with the effective week returned by the backend.
 */
export const getNextFiscalWeek = (
  current?: { period: number; week: number },
  now: Date = new Date()
): NextFiscalWeek => {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  start.setDate(start.getDate() + (7 - start.getDay()));
  const from = toIso(start);
  const date = formatShortDate(start);
  if (!current || !current.period || !current.week) return { from, label: '', display: date };
  const wrap = current.week >= 4;
  const week = wrap ? 1 : current.week + 1;
  const period = wrap ? (current.period >= 13 ? 1 : current.period + 1) : current.period;
  const label = `P${period} W${week}`;
  return { from, label, display: `${label} (${date})` };
};

/** Target in force today for a record: a pending change counts once its effective date has arrived. */
export const getOwnTarget = (
  targets: TargetsMap,
  id: string,
  key: string,
  today: string
): number | null => {
  const p = targets[id]?.pending?.[key];
  if (p && p.from <= today) return p.value;
  return targets[id]?.values?.[key] ?? null;
};

/** A saved change that has not taken effect yet (effective date is after today). */
export const getPendingTarget = (
  targets: TargetsMap,
  id: string,
  key: string,
  today: string
): PendingTarget | null => {
  const p = targets[id]?.pending?.[key];
  return p && p.from > today ? p : null;
};

export const todayIso = (): string => toIso(new Date());
